import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  SovosDialog, SovosDialogContent, SovosDialogActions, SovosFlatButton, SovosDialogTitle
} from 's1-ui';
import LicenseTableCardDisplay from './LicenseTableCardDisplay';

const timeoutPromise = timeout => new Promise(resolve => setTimeout(resolve, timeout));

const styles = {
  dialogText: {
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center'
  },
  dialogOuterSpan: {
    fontSize: '16px',
    color: 'black'
  },
  dialogLicenseNumber: {
    paddingLeft: '5px',
    fontSize: '16px',
    color: 'black'
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 50
  }
};

/* eslint-disable react/prop-types */
const columns = [
  {
    title: 'Ship-To State',
    id: 1,
    component: ({ content }) => <div>{ content.RegionName }</div>
  },
  {
    title: 'License Type',
    id: 2,
    component: ({ content }) => <div>{ content.LicenseName }</div>,
    type: 'greedy'
  },
  {
    title: 'License Number',
    id: 3,
    component: ({ content }) => <div>{ content.LicenseNumber }</div>
  },
  {
    title: 'Effective',
    id: 4,
    component: ({ content }) => <div>{ moment.utc(content.EffectiveDate).format('M[/]D[/]YY') }</div>
  },
  {
    title: 'Expires',
    id: 5,
    component: ({ content }) => <div>{ content.DoesNotExpire || !moment.isDate(content.ExpirationDate) ? 'Does Not Expire' : moment.utc(content.ExpirationDate).format('M[/]D[/]YY') }</div>
  }
];

/* eslint-enable react/prop-types */
class LicenseTableCardContainer extends React.Component {
  static propTypes = {
    loadLicenses: PropTypes.func.isRequired,
    deleteLicense: PropTypes.func.isRequired,
    licenseArray: PropTypes.array,
    onDelete: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    posting: PropTypes.bool

  }

  static defaultProps = {
    licenseArray: undefined,
    loading: undefined,
    posting: undefined

  }

  state = {
    dialogOpen: false,
    license: {
      AccountID: undefined,
      DoesNotExpire: undefined,
      EffectiveDate: undefined,
      ExpirationDate: undefined,
      ID: undefined,
      Number: undefined,
      RegionID: undefined,
      TypeID: undefined,
      RegionName: undefined,
      LicenseName: undefined
    },
    sortedId: 1,
    // eslint-disable-next-line react/no-unused-state
    ascending: true,
    rows: undefined,
    columns
  }

  componentDidMount() {
    this.props.loadLicenses();
  }

  componentDidUpdate(prevProps) {
    if (this.props.licenseArray && this.props.licenseArray !== prevProps.licenseArray) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ rows: this.props.licenseArray });
    }
    if (this.props.posting !== prevProps.posting && this.props.posting === false) {
      this.props.onDelete();
    }
  }

  openDeleteModal = (license) => {
    // this timeout is due to a material-ui bug dealing with Modals
    timeoutPromise(10)
      .then(() => this.setState({ dialogOpen: true, license }));
  }

  onDelete = (id) => {
    this.props.deleteLicense(id);
    this.setState({ dialogOpen: false });
  }

  sort = (column) => {
    this.setState((state) => {
      const ascending = state.sortedId === column.id ? !state.ascending : true;
      const newSortId = (column.id !== 2 && column.id !== 3) ? column.id : state.sortedId;
      const sortedRows = Array.from(state.rows).sort((a, b) => {
        switch (column.id) {
          case 1:
            return this.sortBy(a, b, 'RegionName', ascending);
          case 4:
            return this.sortBy(a, b, 'EffectiveDate', ascending);
          case 5:
            return this.sortBy(a, b, 'ExpirationDate', ascending);
          default:
            return this.state.rows;
        }
      });
      return { rows: sortedRows, sortedId: newSortId, ascending };
    });
  };

  sortBy = (a, b, prop, ascending) => {
    const val1 = ascending ? a[prop] : b[prop];
    const val2 = ascending ? b[prop] : a[prop];

    if (typeof val1 === 'string') {
      return val1.localeCompare(val2, { sensitivity: 'base', ignorePunctuation: true, numeric: true });
    }
    if (val1 === val2) return 0;
    if (val1 > val2) return 1;
    return -1;
  };

  getLicenseEffectivePeriod = (effectiveDate, expirationDate) => (
    <span>
      {'Effective From '}
      { moment.isDate(effectiveDate) ? moment.utc(effectiveDate).format('MM-DD-YYYY') : 'Effective Date is not valid' }
      { moment.isDate(expirationDate) ? ` until ${moment.utc(expirationDate).format('MM-DD-YYYY')}` : ' - Does Not Expire' }
    </span>
  );

  render() {
    const { license } = this.state;
    const { loading, onEdit } = this.props;
    return (
      <div>
        { loading === false
          ? (
            <div>
              { this.state.rows && (
                <LicenseTableCardDisplay
                  columns={ this.state.columns }
                  rows={ this.state.rows }
                  openDeleteModal={ this.openDeleteModal }
                  sort={ this.sort }
                  sortedId={ this.state.sortedId }
                  onEdit={ onEdit }
                  sortAscending={ this.state.ascending }
                />
              )}
              <SovosDialog
                open={ this.state.dialogOpen }
                onClose={ () => this.setState({ dialogOpen: false }) }
                width={ 300 }
                className="CCP__LicenseTableCard__Dialog"
              >
                <SovosDialogTitle>
                  Delete License?
                </SovosDialogTitle>
                <SovosDialogContent style={ styles.dialogText }>
                  <div>
                    <span style={ styles.dialogOuterSpan }>{ license.RegionName }</span><span style={ styles.dialogLicenseNumber }>{ license.Number }</span>
                  </div>
                  <div>{ license.LicenseName }</div>
                  <div>
                    { this.getLicenseEffectivePeriod(license.EffectiveDate, license.ExpirationDate) }
                  </div>
                </SovosDialogContent>
                <SovosDialogActions className="CCP__LicenseTableCard__DialogActions">
                  <SovosFlatButton color="secondary" onClick={ () => this.setState({ dialogOpen: false }) }>Cancel</SovosFlatButton>
                  <SovosFlatButton color="red" onClick={ () => this.onDelete(license.LicenseUid) }>Delete</SovosFlatButton>
                </SovosDialogActions>
              </SovosDialog>
            </div>
          )
          : (
            <div style={ styles.spinner }>
              <CircularProgress size={ 100 } />
            </div>
          )
        }
      </div>
    );
  }
}

export default LicenseTableCardContainer;
